import { useEffect, useState, useCallback } from "react"
import { supabase } from "@/lib/supabase"
import { useToast } from "@/context/ToastContext"
import { getProducts } from "@/services/product.service"
import type { ProductWithStats } from "@/services/product.service"
import { Spinner } from "@/components/common/Spinner"

interface LevelBreakdown {
  level: string
  rate: number
  count: number
  total: number
}

export function EarningsPage() {
  const { toast } = useToast()
  const [products, setProducts]           = useState<ProductWithStats[]>([])
  const [levelData, setLevelData]         = useState<LevelBreakdown[]>([])
  const [thisMonth, setThisMonth]         = useState(0)
  const [lastMonth, setLastMonth]         = useState(0)
  const [lifetime, setLifetime]           = useState(0)
  const [loading, setLoading]             = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [productsData, commRes] = await Promise.all([
        getProducts(),
        supabase.from("commission_transactions").select("amount, type, created_at").eq("status", "paid"),
      ])

      setProducts(productsData)

      const comms = commRes.data ?? []
      const now = new Date()
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1)
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1)

      const tm = comms.filter(c => new Date(c.created_at) >= thisMonthStart)
        .reduce((s, c) => s + (c.amount ?? 0), 0)
      const lm = comms.filter(c => {
        const d = new Date(c.created_at)
        return d >= lastMonthStart && d < thisMonthStart
      }).reduce((s, c) => s + (c.amount ?? 0), 0)
      const lt = comms.reduce((s, c) => s + (c.amount ?? 0), 0)

      setThisMonth(tm)
      setLastMonth(lm)
      setLifetime(lt)

      // Level breakdown from commission types
      const levels: Record<string, { count: number; total: number }> = {}
      for (const c of comms) {
        const key = c.type ?? "other"
        if (!levels[key]) levels[key] = { count: 0, total: 0 }
        levels[key].count++
        levels[key].total += c.amount ?? 0
      }
      setLevelData(
        Object.entries(levels).map(([level, d]) => ({
          level, rate: 0, count: d.count, total: d.total,
        }))
      )
    } catch { toast("Failed to load earnings", "error") }
    finally { setLoading(false) }
  }, [toast])

  useEffect(() => { load() }, [load])

  if (loading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>

  const avg = lifetime > 0 ? lifetime / 12 : 0

  return (
    <div className="space-y-6">
      {/* Top stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "This Month",  value: `$${thisMonth.toFixed(2)}`, color: "bg-green-500" },
          { label: "Last Month",  value: `$${lastMonth.toFixed(2)}`, color: "bg-blue-500" },
          { label: "Avg Monthly", value: `$${avg.toFixed(2)}`,       color: "bg-zimrah-500" },
          { label: "Lifetime",    value: `$${lifetime.toFixed(2)}`,  color: "bg-amber-500" },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className={`h-1 ${s.color}`} />
            <div className="p-5">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-1">{s.label}</p>
              <p className="text-3xl font-black text-gray-900 tabular-nums">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* By commission type */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-50">
            <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">📊 By Type</h2>
          </div>
          {levelData.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-12">No commission data yet</p>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  {["Type", "Transactions", "Total"].map(h => (
                    <th key={h} className="px-6 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-gray-400">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {levelData.map(l => (
                  <tr key={l.level} className="border-t border-gray-50">
                    <td className="px-6 py-3 text-sm font-semibold capitalize text-gray-700">
                      {l.level.replace(/_/g, " ")}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-500 tabular-nums">{l.count}</td>
                    <td className="px-6 py-3 font-black text-gray-900 tabular-nums">${l.total.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* By product */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-50">
            <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">📦 By Product</h2>
          </div>
          {products.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-12">No products yet</p>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  {["Product", "Subscribers", "Revenue/Mo"].map(h => (
                    <th key={h} className="px-6 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-gray-400">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {products.map(p => (
                  <tr key={p.id} className="border-t border-gray-50">
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{p.icon}</span>
                        <span className="font-semibold text-sm text-gray-900">{p.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-500 tabular-nums">{p.activeSubs}</td>
                    <td className="px-6 py-3 font-black text-gray-900 tabular-nums">${p.monthlyRevenue.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}